import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@scio3/ui';
import { DOWNGRADE_TRIAL_TO_FREE, DOWNGRADE_PAID_TO_FREE } from '@scio3/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { useAuthStore } from '@/stores/auth-store';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { useRecordCancelFlowDowngrade } from '../hooks/useRecordCancelFlowDowngrade';
import { useBillingData } from '../hooks/useBillingData';
import { extractMutationResponse } from '../utils/plan-helpers';
import { PAYMENT_STATUS_CODE_LIMIT_QUANTITY } from '../utils/billing-constants';

/**
 * Downgrade confirm dialog — used for PAID → Free downgrades only.
 * Matches scio1's confirmVuexyStyleService.confirm({ text: 'freePlanDowngradeMoveTo(AndCancel)', ... }).
 *
 * Trial → Free does NOT route through this dialog. Per scio1 parity
 * (subscriptionPlanModal.js:1008 setStep short-circuit → onFreePlan trial branch),
 * trial users fire the downgrade mutation directly from useSubscriptionWizard.handleSelectFree
 * with no confirm prompt. See that handler for the trial-flow details.
 *
 * No pre-warning feature list is shown — scio1 bypasses validateChangePlan for the Free path.
 */
export function DowngradeDialog() {
  const { t } = useTranslation();
  const downgradeOpen = useBillingDialogStore((s) => s.downgradeOpen);
  const downgradeContext = useBillingDialogStore((s) => s.downgradeContext);
  const closeDowngrade = useBillingDialogStore((s) => s.closeDowngrade);
  const closeDowngradeComparison = useBillingDialogStore((s) => s.closeDowngradeComparison);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);
  const openScreenManagement = useBillingDialogStore((s) => s.openScreenManagement);
  const recordCancelFlowDowngrade = useRecordCancelFlowDowngrade();

  const { isTrial, currency } = useBillingData();
  const subAccountId = downgradeContext.subAccountId as string | undefined;
  // Only hard-reload when the dialog was opened from the paywall lockout
  // (me.accountStatus === false). In normal app sessions, refetchQueries already
  // updates the UI without disrupting the user.
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);

  const [downgradeTrialToFree, { loading: trialLoading }] = useMutation(DOWNGRADE_TRIAL_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });
  const [downgradePaidToFree, { loading: paidLoading }] = useMutation(DOWNGRADE_PAID_TO_FREE, {
    refetchQueries: [
      'Me',
      'GetCompanyInfo',
      'GetUpcomingSubscription',
      'GetSubscriptionInfoStripe',
    ],
  });

  const isSubmitting = trialLoading || paidLoading;

  const handleDowngrade = useCallback(async () => {
    try {
      if (isTrial) {
        const result = await downgradeTrialToFree({
          variables: { subAccountId, currency },
        });
        const response = extractMutationResponse<{
          status?: boolean;
          message?: string;
          code?: number;
        }>(result.data, 'downgradeTrialToFreePlan');
        if (response?.status) {
          recordCancelFlowDowngrade({ targetPlan: 'free', viaTrial: true });
          toast(t('Your plan has been downgraded to Free'));
          closeDowngrade();
          closeDowngradeComparison();
          closeDialog();
          // Paywall path only: hard reload so every Apollo query, Zustand store, and
          // company-scoped cache refreshes with the now-Free plan state and the
          // isAccountInactive gate lifts. Normal sessions rely on refetchQueries.
          if (isAccountInactive) window.location.reload();
          return;
        }
        // #1244 / #1362 — Trial users hit the same limitFreePlanDeviceDowngrade /
        // LIMIT_QUANTITY (5555) backend check as paid users (scio1
        // changeSubscription.js:213-228). Stack ScreenManagementDialog ON TOP of
        // this dialog so when the user closes it after cleanup they land back here
        // and can retry with one click. (This branch is currently dead code — trial
        // users route through DowngradeComparisonDialog — but mirrors the paid path
        // for safety.) onContinue re-fires handleDowngrade so clicking the dialog's
        // primary action ("Downgrade to Free") completes the original attempt
        // without a back-and-forth.
        if (response?.code === PAYMENT_STATUS_CODE_LIMIT_QUANTITY) {
          openScreenManagement({
            subAccountId,
            messageKey: 'screenManagementModalFreePlan',
            onContinue: () => handleDowngrade(),
          });
          return;
        }
        // Translate known error keys (e.g. limitFreePlanDeviceDowngrade); fall back to raw message.
        toast.error(
          response?.message
            ? t(response.message, { defaultValue: response.message })
            : t('Failed to downgrade'),
        );
      } else {
        const result = await downgradePaidToFree({
          variables: { subAccountId },
        });
        const response = extractMutationResponse<{
          status?: boolean;
          message?: string;
          code?: number;
        }>(result.data, 'downgradePaidToFreePlan');
        if (response?.status) {
          recordCancelFlowDowngrade({ targetPlan: 'free', viaTrial: false });
          toast.success(
            t('Your subscription will be canceled at the end of the current billing period'),
          );
          closeDowngrade();
          closeDowngradeComparison();
          closeDialog();
          if (isAccountInactive) window.location.reload();
          return;
        }
        // #1362 — SP53 / LIMIT_QUANTITY: paid user has unsupported devices on Free
        // (Roku / Firestick / Tizen / iOS / etc.) or too many screens. Stack
        // ScreenManagementDialog ON TOP of this dialog and the comparison dialog.
        // onContinue re-fires handleDowngrade so the dialog's "Downgrade to Free"
        // primary action retries the mutation in-place once devices are cleared.
        if (response?.code === PAYMENT_STATUS_CODE_LIMIT_QUANTITY) {
          openScreenManagement({
            subAccountId,
            messageKey: 'screenManagementModalFreePlan',
            onContinue: () => handleDowngrade(),
          });
          return;
        }
        // Translate known error keys (e.g. limitFreePlanDeviceDowngrade); fall back to raw message.
        toast.error(
          response?.message
            ? t(response.message, { defaultValue: response.message })
            : t('Failed to downgrade'),
        );
      }
    } catch (err) {
      console.error('Downgrade failed:', err);
      toast.error(t('Failed to downgrade'));
    }
  }, [
    isTrial,
    downgradeTrialToFree,
    downgradePaidToFree,
    closeDowngrade,
    closeDowngradeComparison,
    closeDialog,
    openScreenManagement,
    t,
    subAccountId,
    currency,
    isAccountInactive,
    recordCancelFlowDowngrade,
  ]);

  const isOpen = downgradeOpen;
  // scio1 uses different copy for trial users (no existing paid subscription to cancel)
  const confirmPromptKey = isTrial ? 'freePlanDowngradeMoveTo' : 'freePlanDowngradeMoveToAndCancel';
  const confirmButtonKey = isTrial ? 'Downgrade' : 'Confirm Downgrade';
  const titleKey = isTrial ? 'Move to Free Plan?' : 'Downgrade to Free Plan?';

  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting,
    isConfirmed: true,
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDowngrade()}>
      <DialogContent
        className="flex max-h-[85vh] max-w-md flex-col overflow-hidden"
        data-testid="downgrade-dialog"
      >
        {/* No DialogHeader wrapper — its flex row vertically centers the close
            button against multi-line descriptions, which pushes the X out of the
            top-right corner. Stacking title + description in a plain div lets
            the auto-injected absolute close button render flush at top-right
            (reserve right padding so wrapped title text doesn't slide under it). */}
        <div className="space-y-2 pr-8">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
            {t(titleKey)}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line">
            {t(confirmPromptKey)}
          </DialogDescription>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            data-testid="downgrade-dialog-confirm-btn"
            onClick={handleDowngrade}
            disabled={submitDisabled}
            disabledTooltip={disabledTooltip}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t(confirmButtonKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
